// Template-based feature name formatter — parallel implementation to formatBracketsFeature.ts.
// Both implementations coexist for incremental testing and validation.
// The existing formatBracketsFeature.ts remains the active production formatter.

import {
  endsWith,
  findIndex,
  forEach,
  includes,
  join,
  map,
  size,
  slice,
  some,
  split,
  startsWith,
  trim,
} from 'es-toolkit/compat';
import { getRnaCountColumnDisplayName } from 'utils/multiplex/rnaCountColumns';
import {
  bracketsRegex,
  capitalization,
  getDictBrackets,
  getFormattedArea,
  getFormattedCellWithoutPositiveOrNegative,
  getFormattedCellWithStainAndClassifiers,
  getFormattedClassificationModels,
  getFormattedFeatureParam,
  getFormattedQuant,
  getFormattedStain,
  getFormattedText,
} from '..';
import { formatRegistrationFeature, formatRnaInstanceHScoreFeature } from '../formatBracketsFeature';
import { FormatBracketsOptions } from '../formatBracketsOptions';
import { familyTemplates } from './definitions';
import { evaluateWithStain } from './evaluator';
import { FamilyTemplate, SegmentTemplate } from './types';

const COMPARTMENT_GEOMETRY_SUFFIX = '_geometry';

// Custom handlers for families too complex to express as declarative templates.
// These mirror the logic in formatBracketsFeature.ts exactly.
const customHandlers: Record<string, (featureParts: string[], options: FormatBracketsOptions) => string> = {
  '7': (featureParts, bracketTypesOptions) => {
    const dictBrackets = getDictBrackets(featureParts);
    const { areaTypeOptions } = bracketTypesOptions;

    const numeratorCell = getFormattedCellWithStainAndClassifiers(
      dictBrackets['numerator']['c'],
      dictBrackets['s'],
      dictBrackets['numerator']['classifiers'],
      bracketTypesOptions
    );
    const denominatorCell = getFormattedCellWithStainAndClassifiers(
      dictBrackets['denominator']['c'],
      dictBrackets['s'],
      dictBrackets['denominator']['classifiers'],
      bracketTypesOptions
    );

    if (numeratorCell === denominatorCell) {
      return `Fold change ${numeratorCell} fraction out of all cells in ${getFormattedArea(
        dictBrackets['numerator']['a'],
        areaTypeOptions
      )} vs. ${getFormattedArea(dictBrackets['denominator']['a'], areaTypeOptions)}`;
    }

    return `${numeratorCell} fraction out of all cells in ${getFormattedArea(
      dictBrackets['numerator']['a'],
      areaTypeOptions
    )} to ${denominatorCell} fraction out of all cells in ${getFormattedArea(
      dictBrackets['denominator']['a'],
      areaTypeOptions
    )} ratio`;
  },

  '18': (featureParts, bracketTypesOptions) => {
    const dictBrackets = getDictBrackets(featureParts);
    const { stainTypeOptions } = bracketTypesOptions;

    const sourcesStain = map(dictBrackets['sources_s'], (source_s: string) => {
      return `${getFormattedStain(
        source_s,
        stainTypeOptions,
        bracketTypesOptions.rnaProbeDisplayNameOverridesByProbeId
      )}+`;
    });

    return `${getFormattedCellWithStainAndClassifiers(
      getFormattedCellWithoutPositiveOrNegative(dictBrackets['subset']?.['c']),
      dictBrackets['subset']?.['s'],
      dictBrackets['subset']?.['classifiers'],
      bracketTypesOptions
    )} coexpression of (${join(sourcesStain, ', ')}, ${getFormattedStain(
      dictBrackets['target_s']['s'],
      stainTypeOptions,
      bracketTypesOptions.rnaProbeDisplayNameOverridesByProbeId
    )}+) by joint density (sigma: ${dictBrackets['sigma']}, thresh: ${dictBrackets['thresh']})`;
  },

  '32': (featureParts, bracketTypesOptions) => {
    const dictBrackets = getDictBrackets(featureParts);
    const { classificationModelOptions, areaTypeOptions, stainTypeOptions } = bracketTypesOptions;
    const formattedClassificationModels = includes(dictBrackets['cm'], 'quant')
      ? ` on ${getFormattedQuant(
          dictBrackets['cm'],
          dictBrackets['s'],
          stainTypeOptions,
          bracketTypesOptions.rnaProbeDisplayNameOverridesByProbeId
        )}`
      : dictBrackets['cm'] && dictBrackets['cm'] !== 'intensity'
      ? ` (${getFormattedClassificationModels(dictBrackets['cm'], classificationModelOptions)})`
      : '';

    return `H score ${getFormattedCellWithStainAndClassifiers(
      dictBrackets['pos']['c'],
      dictBrackets['s'],
      dictBrackets?.['pos']?.['classifiers'],
      bracketTypesOptions
    )}${formattedClassificationModels} in ${getFormattedArea(dictBrackets['a'], areaTypeOptions)}`;
  },

  '43': (featureParts, bracketTypesOptions) => {
    const dictBrackets = getDictBrackets(featureParts);
    const { areaTypeOptions } = bracketTypesOptions;

    if (
      dictBrackets['numerator']['neg']['c'] === 'tumor_cell-negative' &&
      (dictBrackets['denominator']['c'] === 'tumor_cell' ||
        dictBrackets['denominator']['c'] === 'tumor_cell-negative|tumor_cell-positive') &&
      dictBrackets['a'] === '1'
    ) {
      return `SPS for ${getFormattedCellWithStainAndClassifiers(
        dictBrackets['numerator']['pos']['c'],
        dictBrackets['s'],
        dictBrackets['numerator']['pos']['classifiers'],
        bracketTypesOptions
      )} (max radius: ${dictBrackets['max_dist']}, min neighbors: ${dictBrackets['min_neighbors']})`;
    }

    return `SPS for ${getFormattedCellWithStainAndClassifiers(
      dictBrackets['numerator']['pos']['c'],
      dictBrackets['s'],
      dictBrackets['numerator']['pos']['classifiers'],
      bracketTypesOptions
    )} with ${getFormattedCellWithStainAndClassifiers(
      dictBrackets['denominator']['c'],
      dictBrackets['s'],
      dictBrackets['denominator']['classifiers'],
      bracketTypesOptions
    )} in ${getFormattedArea(dictBrackets['a'], areaTypeOptions)} (negative cells: ${trim(
      getFormattedCellWithStainAndClassifiers(
        dictBrackets['numerator']['neg']['c'],
        dictBrackets['s'],
        dictBrackets['numerator']['neg']['classifiers'],
        bracketTypesOptions
      )
    )}, max radius: ${dictBrackets['max_dist']}, min neighbors: ${dictBrackets['min_neighbors']})`;
  },

  '44': (featureParts, bracketTypesOptions) => {
    const dictBrackets = getDictBrackets(featureParts);
    const { areaTypeOptions, stainTypeOptions, featureParams } = bracketTypesOptions;

    const reduction = getFormattedFeatureParam(dictBrackets['reduction'], featureParams);
    const column = includes(dictBrackets['column'], 'quant')
      ? `${getFormattedQuant(
          dictBrackets['column'],
          dictBrackets['s'],
          stainTypeOptions,
          bracketTypesOptions.rnaProbeDisplayNameOverridesByProbeId
        )}`
      : getRnaCountColumnDisplayName(
          dictBrackets['column'],
          (identifier) => bracketTypesOptions.rnaProbeDisplayNameOverridesByProbeId?.[identifier] || identifier
        ) || dictBrackets['column'];
    const cellClass = getFormattedCellWithStainAndClassifiers(
      dictBrackets['c'],
      dictBrackets['s'],
      dictBrackets['classifiers'],
      bracketTypesOptions
    );
    const area = getFormattedArea(dictBrackets['a'], areaTypeOptions);

    let result = `${reduction} ${column} for ${cellClass} in ${area}`;

    if (dictBrackets['source_s']) {
      const stain = getFormattedStain(
        dictBrackets['source_s'],
        stainTypeOptions,
        bracketTypesOptions.rnaProbeDisplayNameOverridesByProbeId
      );
      result += ` (from ${stain})`;
    }

    return result;
  },

  '46': (featureParts, bracketTypesOptions) => {
    const dictBrackets = getDictBrackets(featureParts);
    const { areaTypeOptions, featureParams, stainTypeOptions } = bracketTypesOptions;

    const columnParts = split(dictBrackets['column'], '.');
    const quantIndex = findIndex(columnParts, (part) => part === 'quant');
    const markerIndex = findIndex(columnParts, (part) => part === 'marker');

    const afterQuantWord = quantIndex !== -1 && quantIndex + 1 < size(columnParts) ? columnParts[quantIndex + 1] : '';
    const afterMarkerWord =
      markerIndex !== -1 && markerIndex + 1 < size(columnParts) ? columnParts[markerIndex + 1] : '';
    const formattedStainMarker = getFormattedStain(
      afterMarkerWord,
      stainTypeOptions,
      bracketTypesOptions.rnaProbeDisplayNameOverridesByProbeId
    );

    return `${getFormattedFeatureParam(
      dictBrackets['reduction'],
      featureParams
    )} ${formattedStainMarker} ${getFormattedText(afterQuantWord)} in ${getFormattedArea(
      dictBrackets['a'],
      areaTypeOptions
    )} in ${getFormattedArea(dictBrackets['containing_a'], areaTypeOptions)}`;
  },

  '47': (featureParts, bracketTypesOptions) => {
    const dictBrackets = getDictBrackets(featureParts);
    const { areaTypeOptions, featureParams } = bracketTypesOptions;

    const reduction = getFormattedFeatureParam(dictBrackets['reduction'], featureParams);
    const detectionClass = dictBrackets['detection_class']
      ? `${
          bracketTypesOptions.rnaProbeDisplayNameOverridesByProbeId?.[dictBrackets['detection_class']] ||
          dictBrackets['detection_class']
        } `
      : '';
    const cellClass = getFormattedCellWithStainAndClassifiers(
      dictBrackets['c'],
      dictBrackets['s'],
      dictBrackets['classifiers'],
      bracketTypesOptions
    );
    const area = getFormattedArea(dictBrackets['containing_a'], areaTypeOptions);
    const rawCompartment = dictBrackets['compartment'] ? `${dictBrackets['compartment']}` : '';
    const compartment = rawCompartment
      ? getFormattedText(
          endsWith(rawCompartment, COMPARTMENT_GEOMETRY_SUFFIX)
            ? rawCompartment.slice(0, -size(COMPARTMENT_GEOMETRY_SUFFIX))
            : rawCompartment
        )
      : '';
    const dilation = dictBrackets['dilation'];

    return `${reduction} ${detectionClass}count in ${cellClass} in ${area} (${compartment} compartment dilated ${dilation} um)`;
  },
  '49': formatRnaInstanceHScoreFeature,
};

const resolveTemplate = (familyId: string): FamilyTemplate | undefined => {
  const template = familyTemplates[familyId];
  if (!template) return undefined;
  if (template.type === 'alias') return resolveTemplate(template.target);
  return template;
};

const evaluateFamily = (
  familyId: string,
  featureParts: string[],
  dictBrackets: Record<string, any>,
  options: FormatBracketsOptions
): string | undefined => {
  const template = resolveTemplate(familyId);
  if (!template) return undefined;

  if (template.type === 'custom') {
    const familyTemplate = familyTemplates[familyId];
    const aliasTarget = familyTemplate?.type === 'alias' ? familyTemplate.target : undefined;
    const handler = customHandlers[familyId] || (aliasTarget ? customHandlers[aliasTarget] : undefined);
    if (!handler) return undefined;
    return handler(featureParts, options);
  }

  const segmentTemplate = template as SegmentTemplate;
  return evaluateWithStain(segmentTemplate.segments, dictBrackets, options, !!segmentTemplate.applyStain);
};

export const formatBracketsFeatureTemplated = (key: string, options: FormatBracketsOptions): string => {
  try {
    const featureParts = slice(split(key, bracketsRegex), 1, -1);
    let family = '';
    forEach(featureParts, (featurePart) => {
      if (startsWith(featurePart, 'f:')) {
        family = split(featurePart, ':')[1];
      }
    });

    if (family === '') {
      if (some(featureParts, (featurePart) => startsWith(featurePart, 'r:'))) {
        return formatRegistrationFeature(featureParts, options);
      }
    }

    const dictBrackets = getDictBrackets(featureParts);
    const result = evaluateFamily(family, featureParts, dictBrackets, options);

    if (result === undefined) return key;
    return capitalization(result);
  } catch (error) {
    console.error(`Error formatting feature (templated): ${key}`, error);
    return key;
  }
};
